import React, { Fragment, useEffect, useState } from 'react';
import {
  useGetCrewDeliveryDetailsQuery,
  useCancelCrewDeliveryMutation,
} from '../../../redux/api/crewDeliveryApi';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';
import FormatDateTime from '../../../components/dateTime/FormatDateTime';
import {
  faBan,
  faClipboardCheck,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CrewDeliveryPdfButtons from './CrewDeliveryPdfButtons';
import ConfirmCrewDelivery from '../confirm/ConfirmCrewDelivery';
import { useApiErrorToast } from '../../../hooks/useApiError';

const CrewDeliveryDetails = ({ confirm = false }) => {
  const { deliveryId } = useParams();

  const { data: deliveryData } = useGetCrewDeliveryDetailsQuery(
    deliveryId ?? skipToken
  );

  const [delivery, setDelivery] = useState(null);
  const [deliveryLines, setDeliveryLines] = useState([]);
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();
  const [cancelDelivery, { isLoading: isCancelling }] =
    useCancelCrewDeliveryMutation();

  const isReturn = delivery?.type === 'RET';
  const docLabel = isReturn ? 'Retoure' : 'Lieferung';
  const actualLabel = isReturn ? 'zurückgenommene Menge' : 'gelieferte Menge';

  const handleCancel = async () => {
    if (
      !window.confirm(
        isReturn
          ? 'Retoure wirklich stornieren?'
          : 'Lieferung wirklich stornieren? Die komplette Lieferung wird zurückgebucht.'
      )
    ) {
      return;
    }
    try {
      await cancelDelivery({ id: deliveryId }).unwrap();
      toast.success(`${docLabel} wurde storniert!`);
      navigate('/');
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Stornieren!');
    }
  };

  useEffect(() => {
    if (deliveryData) {
      setDelivery(deliveryData);

      setDeliveryLines(
        deliveryData.gkapi_crewdelivery_lines.map((line) => ({
          id: line.id,
          destination_name: line.destination_name,
          article_name: line.article_name,
          planned_quantity: line.planned_quantity,
          actual_quantity: line.actual_quantity,
        }))
      );
    }
  }, [deliveryData]);

  const handlePlannedQuantities = () => {
    setDeliveryLines(
      deliveryLines.map((line) => ({
        id: line.id,
        destination_name: line.destination_name,
        article_name: line.article_name,
        planned_quantity: line.planned_quantity,
        actual_quantity: line.planned_quantity,
      }))
    );
  };

  return delivery ? (
    <Fragment>
      <h4>
        {docLabel} #{delivery.counter}
        {isReturn && (
          <span className='badge bg-warning text-dark ms-2'>Retoure</span>
        )}
      </h4>
      <CrewDeliveryPdfButtons deliveryId={deliveryId} />
      {!confirm && !delivery.confirmed && (
        <Fragment>
          <button
            className='btn btn-info ms-2'
            onClick={() => navigate(`/crew-delivery/${deliveryId}/confirm`)}
          >
            <FontAwesomeIcon icon={faClipboardCheck} className='me-2' />
            {docLabel} bestätigen
          </button>
          <button
            className='btn btn-outline-danger ms-2'
            onClick={handleCancel}
            disabled={isCancelling}
          >
            <FontAwesomeIcon icon={faBan} className='me-2' />
            {docLabel} stornieren
          </button>
        </Fragment>
      )}
      <div>
        <table className='table table-striped mt-4'>
          <tbody>
            <tr>
              <td>ID Lieferung</td>
              <td>
                Lieferung #{delivery.counter} / {delivery.id}
              </td>
            </tr>
            <tr>
              <td>angelegt</td>
              <td>
                <FormatDateTime dateTime={delivery.created_at} /> von{' '}
                <b>{delivery.created_by_username}</b>
              </td>
            </tr>
            {delivery.cancelled ? (
              <tr>
                <td>storniert</td>
                <td className='text-danger'>
                  <b>STORNIERT</b> —{' '}
                  <FormatDateTime dateTime={delivery.cancelled_at} /> von{' '}
                  <b>{delivery.cancelled_by_username}</b>
                </td>
              </tr>
            ) : (
              delivery.confirmed && (
                <tr>
                  <td>bestätigt</td>
                  <td>
                    <FormatDateTime dateTime={delivery.confirmed_at} /> von{' '}
                    <b>{delivery.confirmed_by_username}</b>
                  </td>
                </tr>
              )
            )}

            {delivery.comment.length > 0 && (
              <tr>
                <td>Kommentar/Hinweis</td>
                <td>
                  <p className='text-danger'>{delivery.comment}</p>
                </td>
              </tr>
            )}

            {delivery.delivery_note_comment?.length > 0 && (
              <tr>
                <td>Lieferschein-Hinweis (gedruckt)</td>
                <td>{delivery.delivery_note_comment}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='mt-5 text-start'>
        <h4>{isReturn ? 'Retoure-Positionen' : 'Lieferpositionen'}</h4>
        {confirm && (
          <button
            className='btn btn-warning ms-2'
            onClick={handlePlannedQuantities}
            disabled={!deliveryLines}
          >
            <FontAwesomeIcon icon={faCopy} className='me-2' />
            Alle Mengen wie geplant
          </button>
        )}

        <table className='table table-striped mt-4'>
          <thead>
            <tr>
              <th>Lieferstelle</th>
              <th>Artikel</th>
              <th>geplante Menge</th>
              {confirm ? <th>{actualLabel}</th> : null}
              {delivery.confirmed && <th>{actualLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {deliveryLines?.map((line, index) => (
              <tr key={index}>
                <td>{line.destination_name}</td>
                <td>{line.article_name}</td>
                <td>{line.planned_quantity}</td>
                {confirm && (
                  <td>
                    <input
                      className={`form-control${
                        line.actual_quantity === '' ||
                        line.actual_quantity === null ||
                        line.actual_quantity === undefined
                          ? ' is-invalid'
                          : ''
                      }`}
                      type='number'
                      min='0'
                      value={line.actual_quantity ?? ''}
                      onChange={(event) => {
                        const raw = event.target.value;
                        // Keep an empty field as '' (unfilled) rather than
                        // coercing to 0, so the confirm button can stay
                        // blocked until every line has an explicit quantity.
                        const newQuantity =
                          raw === '' ? '' : Math.max(0, parseInt(raw, 10) || 0);
                        setDeliveryLines((prevLines) =>
                          prevLines.map((prevLine) =>
                            prevLine.id === line.id
                              ? { ...prevLine, actual_quantity: newQuantity }
                              : prevLine
                          )
                        );
                      }}
                    />
                  </td>
                )}
                {delivery.confirmed && <td>{line.actual_quantity}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirm ? (
        <ConfirmCrewDelivery
          deliveryLines={deliveryLines}
          deliveryId={deliveryId}
          isReturn={isReturn}
        />
      ) : null}
    </Fragment>
  ) : null;
};

export default CrewDeliveryDetails;
